import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faKeyboard,
  faCube,
  faCartShopping,
  faUserGroup,
  faMagnifyingGlass,
  faFileLines,
  faPercent,
  faTrashCan,
  faPause,
  faCircleCheck,
  faPrint,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons';

interface FunctionKeysModalProps {
  modalId?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

const FunctionKeysModal: React.FC<FunctionKeysModalProps> = ({
  modalId = 'functionKeysModal',
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  const productShortcuts = [
    {
      title: t('Search Product') || 'Search Product',
      desc: t('Search by name or scan barcode') || 'Search by name\nor scan barcode',
      key: 'F1',
      icon: faMagnifyingGlass,
      iconColorClass: 'text-primary',
      bgClass: 'bg-soft-blue',
    },
    {
      title: t('Product Details') || 'Product Details',
      desc: t('View selected product details') || 'View selected product details',
      key: 'F2',
      icon: faFileLines,
      iconColorClass: 'text-primary',
      bgClass: 'bg-soft-blue',
    },
    {
      title: t('Add to Cart') || 'Add to Cart',
      desc: t('Add currently selected product to cart') || 'Add currently\nselected product\nto cart',
      key: 'F3',
      icon: faCartShopping,
      iconColorClass: 'text-success',
      bgClass: 'bg-soft-green',
    },
  ];

  const cartShortcuts = [
    {
      title: t('Cart Discount') || 'Cart Discount',
      desc: t('Apply discount to entire cart') || 'Apply discount to entire cart',
      key: 'F4',
      icon: faPercent,
      iconColorClass: 'icon-orange',
      bgClass: 'bg-soft-peach',
    },
    {
      title: t('Clear Cart') || 'Clear Cart',
      desc: t('Remove all items from cart') || 'Remove all items from cart',
      key: 'F5',
      icon: faTrashCan,
      iconColorClass: 'icon-pink',
      bgClass: 'bg-soft-pink',
    },
    {
      title: t('Hold Cart') || 'Hold Cart',
      desc: t('Save current cart temporarily') || 'Save current cart temporarily',
      key: 'F6',
      icon: faPause,
      iconColorClass: 'text-dark',
      bgClass: 'bg-soft-slate',
    },
    {
      title: t('Place Order') || 'Place Order',
      desc: t('Complete billing and payment') || 'Complete billing and payment',
      key: 'F7',
      icon: faCircleCheck,
      iconColorClass: 'text-success',
      bgClass: 'bg-soft-green',
    },
    {
      title: t('Print Invoice') || 'Print Invoice',
      desc: t('Print last completed order') || 'Print last completed order',
      key: 'F8',
      icon: faPrint,
      iconColorClass: 'text-primary',
      bgClass: 'bg-soft-blue',
    },
  ];

  const customerShortcuts = [
    {
      title: t('Search Customer') || 'Search Customer',
      desc: t('Find and select existing customer') || 'Find and select existing customer',
      key: 'F9',
      icon: faMagnifyingGlass,
      iconColorClass: 'text-primary',
      bgClass: 'bg-soft-blue',
    },
    {
      title: t('New Customer') || 'New Customer',
      desc: t('Create new customer') || 'Create new customer',
      key: 'F10',
      icon: faUserPlus,
      iconColorClass: 'text-success',
      bgClass: 'bg-soft-green',
    },
  ];

  const modalContent = (
    <div
      className={`modal fade ${isOpen ? 'show d-block' : ''}`}
      id={modalId}
      tabIndex={-1}
      aria-labelledby={`${modalId}Label`}
      aria-hidden={!isOpen}
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered modal-xl fk-modal">
        <div className="modal-content border-0 rounded-1 shadow-lg bg-white">
          <div className="modal-header border-bottom border-gray-200 px-4 py-4 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <div className="fk-icon-badge d-flex align-items-center justify-content-center text-white rounded-1 shadow-sm fs-5">
                <FontAwesomeIcon icon={faKeyboard} />
              </div>
              <div>
                <h5 className="mb-0 fw-bold text-dark" id={`${modalId}Label`}>{t('Function Keys') || 'Function Keys'}</h5>
                <p className="text-muted mb-0">
                  {t('Use keyboard shortcuts to work faster in POS') ||
                    'Use keyboard shortcuts to work faster in POS'}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={onClose}
            />
          </div>

          {/* Body */}
          <div className="modal-body px-4 py-4 d-flex flex-column gap-3">
            {/* Section 1: Product */}
            <div>
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="text-primary fs-6">
                  <FontAwesomeIcon icon={faCube} size="xl" />
                </span>
                <h5 className="fw-bold text-dark mb-0">{t('Product') || 'Product'}</h5>
              </div>
              <div className="row row-cols-1 row-cols-md-3 g-3">
                {productShortcuts.map((item) => (
                  <div key={item.key} className="col">
                    <div
                      className={`fk-card rounded-1 p-3 text-center d-flex flex-column align-items-center justify-content-between h-100 ${item.bgClass}`}
                    >
                      <div className={`fs-3 mb-1 ${item.iconColorClass}`}>
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <div className="mb-2">
                        <h6 className="fw-bold text-dark mb-1">{item.title}</h6>
                        <div className="text-muted fk-desc">{item.desc}</div>
                      </div>
                      <span className="fk-badge fw-bold rounded-1">{item.key}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 2: Cart & Checkout */}
            <div>
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="text-primary fs-6">
                  <FontAwesomeIcon icon={faCartShopping} size="xl" />
                </span>
                <h5 className="fw-bold text-dark mb-0">
                  {t('Cart & Checkout') || 'Cart & Checkout'}
                </h5>
              </div>
              <div className="row row-cols-2 row-cols-md-5 g-2 g-md-3">
                {cartShortcuts.map((item) => (
                  <div key={item.key} className="col">
                    <div
                      className={`fk-card rounded-1 p-3 text-center d-flex flex-column align-items-center justify-content-between h-100 ${item.bgClass}`}
                    >
                      <div className={`fs-3 mb-1 ${item.iconColorClass}`}>
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <div className="mb-2">
                        <h6 className="fw-bold text-dark mb-1">{item.title}</h6>
                        <div className="text-muted fk-desc">{item.desc}</div>
                      </div>
                      <span className="fk-badge fw-bold rounded-1">{item.key}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 3: Customer (Icons placed on top, matching Product layout) */}
            <div>
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="text-primary fs-6">
                  <FontAwesomeIcon icon={faUserGroup} size="xl" />
                </span>
                <h5 className="fw-bold text-dark mb-0">{t('Customer') || 'Customer'}</h5>
              </div>
              <div className="row row-cols-1 row-cols-md-3 g-3">
                {customerShortcuts.map((item) => (
                  <div key={item.key} className="col">
                    <div
                      className={`fk-card rounded-1 p-3 text-center d-flex flex-column align-items-center justify-content-between h-100 ${item.bgClass}`}
                    >
                      <div className={`fs-3 mb-1 ${item.iconColorClass}`}>
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <div className="mb-2">
                        <h6 className="fw-bold text-dark mb-1">{item.title}</h6>
                        <div className="text-muted fk-desc">{item.desc}</div>
                      </div>
                      <span className="fk-badge fw-bold rounded-1">{item.key}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(modalContent, document.body);
};

export default FunctionKeysModal;
